import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import api from '../../../../api/axios';
import Cookies from 'js-cookie';
import figureConverter from '../../../../tools/figureConverter';
import {useDatePickerStore} from '../../card/DatepickerStore';
import {format} from 'date-fns';
import {MagnifyingGlassIcon} from '@heroicons/react/24/solid';

const itemsPerPage = 14; // Anzahl der Projekte pro Seite

const ProjectChart = ({title}) => {
    const {hapakid} = useParams();
    const [projectData, setProjectData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState('subject');
    const [sortOrder, setSortOrder] = useState('asc');
    const [markedProjects, setMarkedProjects] = useState([]);
    const [showMarked, setShowMarked] = useState(false);
    const [statusFilters, setStatusFilters] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const startDate = useDatePickerStore((state) => state.startDate);
    const endDate = useDatePickerStore((state) => state.endDate);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const accessToken = Cookies.get('accessToken');
                if (!accessToken) {
                    throw new Error('Access Token fehlt.');
                }

                const formattedStartDate = format(startDate, 'yyyy-MM-dd');
                const formattedEndDate = format(endDate, 'yyyy-MM-dd');

                const response = await api.get(`/api/v1/analysis/sitemanager/${hapakid}?start=${formattedStartDate}&end=${formattedEndDate}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                const {projects} = response.data;
                const formattedData = projects.map((project) => ({
                    number: project.number,
                    subject: project.subject,
                    revenue: parseFloat(project.revenue),
                    costs: parseFloat(project.costs),
                    yield: parseFloat(project.yield),
                    state: project.state,
                }));

                setProjectData(formattedData);
                setLoading(false);
            } catch (error) {
                console.error('Fehler beim Abrufen der Daten:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [hapakid, startDate, endDate]);

    if (loading) {
        return <div>Lade Daten...</div>;
    }

    const truncate = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    const allStatusValues = [...new Set(projectData.map(project => project.state))];

    const handleStatusChange = (status) => {
        if (statusFilters.includes(status)) {
            setStatusFilters(statusFilters.filter(s => s !== status));
        } else {
            setStatusFilters([...statusFilters, status]);
        }
    };

    const removeStatusFilter = (status) => {
        setStatusFilters(statusFilters.filter(s => s !== status));
    };

    const filteredProjects = projectData.filter((project) => {
        const matchesSearchTerm = project.subject.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilters.length === 0 || statusFilters.includes(project.state);
        return matchesSearchTerm && matchesStatus;
    });

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        if (sortOrder === 'asc') {
            return a[sortField] > b[sortField] ? 1 : -1;
        } else {
            return a[sortField] < b[sortField] ? 1 : -1;
        }
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedProjects = sortedProjects.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleRowClick = (project) => {
        if (markedProjects.includes(project)) {
            setMarkedProjects(markedProjects.filter((p) => p !== project));
        } else {
            setMarkedProjects([...markedProjects, project]);
        }
    };

    const projectsToShow = showMarked ? markedProjects : displayedProjects;

    const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 8;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1, 2);
            if (currentPage > 4) pageNumbers.push('...');
            for (let i = Math.max(currentPage - 1, 3); i <= Math.min(currentPage + 1, totalPages - 2); i++) {
                pageNumbers.push(i);
            }
            if (currentPage < totalPages - 3) pageNumbers.push('...');
            pageNumbers.push(totalPages - 1, totalPages);
        }

        return pageNumbers.map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    onClick={() => setCurrentPage(page)}
                    className={`ml-2 px-3 py-2 w-fit font-color-1 text-sm flex items-center text-center shadow-sm border border-color-2 rounded-lg font-medium ${currentPage === page ? '' : 'bg-color-5 font-color-2'
                    }`}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="px-2"></span>
            )
        );
    };

    return (
        <div className="w-full flex flex-col justify-start pt-2 text-sm">
            <div className='flex justify-between items-center'>
                <p className='font-light font-color-4'>{title}</p>

                {/* Suchfeld */}
                <div className="flex px-3 py-2 bg-color-5 rounded-full items-center shadow-sm border border-color-2">
                    <MagnifyingGlassIcon className="w-5 h-5 font-color-1"/>
                    <input
                        type="text"
                        className="text-black text-sm font-light w-full bg-color-5 ml-2 focus:outline-none"
                        placeholder="Suche nach Projekt..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className='flex justify-between items-center font-color-4 text-ms mt-4'>

                <div className='flex items-center'>
                    <span className='mr-2'>Markierte Projekte anzeigen</span>
                    <div
                        className={`relative inline-flex items-center cursor-pointer w-8 h-4 rounded-full transition-colors ${
                            showMarked ? 'bg-color-1' : 'bg-gray-200'
                        }`}
                        onClick={() => setShowMarked(!showMarked)} // Toggle-Funktion
                    >
                        <div
                            className={`dot absolute w-3 h-3 bg-white rounded-full transition-transform transform ${
                                showMarked ? 'translate-x-5' : 'translate-x-0'
                            }`}
                        ></div>
                    </div>
                </div>


                {/* Filter nach Status als Dropdown */}
                <div className="relative">
                    <div
                        className="bg-color-5 px-5 px-3 py-2 border rounded-lg cursor-pointer text-center"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        Filter nach Status
                    </div>
                    {dropdownOpen && (
                        <div className="absolute mt-2 bg-white border rounded-lg shadow-md z-10 w-full">
                            {allStatusValues.map((status, index) => (
                                <div key={index} className="px-3 py-1 hover:bg-gray-200 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={statusFilters.includes(status)}
                                        onChange={() => handleStatusChange(status)}
                                        className="mr-2"
                                    />
                                    <label>{status}</label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <table className="min-w-full table-auto border-collapse mt-4">
                <thead>
                <tr>
                    <th onClick={() => handleSort('number')} className="cursor-pointer text-left px-2">Projekt-ID</th>
                    <th onClick={() => handleSort('subject')} className="cursor-pointer text-left px-2">Projekt</th>
                    <th onClick={() => handleSort('revenue')} className="cursor-pointer text-right px-2">Umsatz</th>
                    <th onClick={() => handleSort('costs')} className="cursor-pointer text-right px-2">Kosten</th>
                    <th onClick={() => handleSort('yield')} className="cursor-pointer text-right px-2">Marge</th>
                    <th onClick={() => handleSort('state')} className="cursor-pointer text-left px-2">Status</th>
                </tr>
                </thead>
                <tbody>
                {projectsToShow.map((project, index) => (
                    <tr
                        key={index}
                        className={`border-t ${markedProjects.includes(project) ? 'bg-blue-200' : ''} hover:bg-gray-100`}
                        onClick={() => handleRowClick(project)}
                        style={{cursor: 'pointer'}}
                    >
                        <td className="text-left px-2 py-1">{project.number}</td>
                        <td className="text-left px-2 py-1">{truncate(project.subject, 70)}</td>
                        <td className="text-right px-2">{figureConverter(project.revenue, false)}</td>
                        <td className="text-right px-2">{figureConverter(project.costs, false)}</td>
                        <td className="text-right px-2">{figureConverter(project.yield, false)}</td>
                        <td className="text-left px-2">{project.state}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            <div className="flex justify-center mt-4">
                <button
                    onClick={handlePrevPage}
                    className="ml-2 px-3 py-2 w-fit font-color-1 text-sm flex items-center text-center shadow-sm border border-color-2 rounded-lg font-medium"
                    disabled={currentPage === 1}
                >
                    &lt;
                </button>
                {renderPageNumbers()}
                <button
                    onClick={handleNextPage}
                    className="ml-2 px-3 py-2 w-fit font-color-1 text-sm flex items-center text-center shadow-sm border border-color-2 rounded-lg font-medium"
                    disabled={currentPage === totalPages}
                >
                    &gt;
                </button>
            </div>
        </div>
    );
};

export default ProjectChart;
